import React, { useCallback, useEffect, useState } from 'react';
import { FlaskConical, Loader2, X } from 'lucide-react';
import type { AiModelOption, AiPromptTemplate, AiTestProject, AiTestRun } from './types';
import { listTesterModels, listTesterProjects, listTesterPrompts, listTesterRuns } from './api';
import SourceOfTruthForm from './SourceOfTruthForm';
import JtbdForm from './JtbdForm';
import TensionForm from './TensionForm';
import RecipeAttributesForm from './RecipeAttributesForm';
import WrittenConceptForm from './WrittenConceptForm';
import VisualConceptForm from './VisualConceptForm';
import VisualEditForm from './VisualEditForm';
import RunHistory from './RunHistory';

interface AiTesterPanelProps {
  onClose: () => void;
}

const GENERATORS = [
  { key: 'source_of_truth', label: 'Source of Truth', enabled: true },
  { key: 'jtbd', label: 'Jobs to be Done', enabled: true },
  { key: 'tension', label: 'Tension', enabled: true },
  { key: 'recipe_attributes', label: 'Recipe Card Attributes', enabled: true },
  { key: 'written_concept', label: 'Written Concept', enabled: true },
  { key: 'visual_concept', label: 'Visual Concept', enabled: true },
  { key: 'visual_concept_regenerate', label: 'Visual Concept Regenerate', enabled: true },
  { key: 'visual_concept_edit', label: 'Visual Concept Edit', enabled: true },
] as const;

const AiTesterPanel: React.FC<AiTesterPanelProps> = ({ onClose }) => {
  const [generator, setGenerator] = useState('source_of_truth');
  const [projects, setProjects] = useState<AiTestProject[]>([]);
  const [models, setModels] = useState<AiModelOption[]>([]);
  const [prompts, setPrompts] = useState<AiPromptTemplate[]>([]);
  const [runs, setRuns] = useState<AiTestRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshPrompts = useCallback(async () => {
    const rows = await listTesterPrompts(generator);
    setPrompts(rows);
  }, [generator]);

  const refreshRuns = useCallback(async () => {
    const rows = await listTesterRuns(undefined, generator);
    setRuns(rows);
  }, [generator]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [projectRows, modelRows] = await Promise.all([
          listTesterProjects(),
          listTesterModels(generator),
        ]);
        if (cancelled) return;
        setProjects(projectRows);
        setModels(modelRows);
        await refreshPrompts();
        await refreshRuns();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load tester data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [generator, refreshPrompts, refreshRuns]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E8E8]">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-[#3da3d4]" />
          <h2 className="text-base font-semibold text-[#0C0B0B]">AI Prompt Testing</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-[#F3F3F3]"
          aria-label="Close AI prompt testing"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
        <div>
          <label className="block text-[12px] font-medium text-[#626262] mb-1">Generator</label>
          <select
            value={generator}
            onChange={(e) => setGenerator(e.target.value)}
            className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
          >
            {GENERATORS.map((item) => (
              <option key={item.key} value={item.key} disabled={!item.enabled}>
                {item.label}
                {item.enabled ? '' : ' (coming next)'}
              </option>
            ))}
          </select>
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && generator === 'source_of_truth' && (
          <>
            <SourceOfTruthForm
              projects={projects}
              models={models}
              prompts={prompts}
              onPromptsChanged={refreshPrompts}
              onRunStarted={refreshRuns}
            />
            <div>
              <h3 className="text-sm font-semibold text-[#0C0B0B] mb-3">Run history</h3>
              <RunHistory runs={runs} models={models} onRefresh={refreshRuns} />
            </div>
          </>
        )}
        {!loading && generator === 'jtbd' && (
          <>
            <JtbdForm
              projects={projects}
              models={models}
              prompts={prompts}
              onPromptsChanged={refreshPrompts}
              onRunStarted={refreshRuns}
            />
            <div>
              <h3 className="text-sm font-semibold text-[#0C0B0B] mb-3">Run history</h3>
              <RunHistory runs={runs} models={models} onRefresh={refreshRuns} />
            </div>
          </>
        )}
        {!loading && generator === 'tension' && (
          <>
            <TensionForm
              projects={projects}
              models={models}
              prompts={prompts}
              onPromptsChanged={refreshPrompts}
              onRunStarted={refreshRuns}
            />
            <div>
              <h3 className="text-sm font-semibold text-[#0C0B0B] mb-3">Run history</h3>
              <RunHistory runs={runs} models={models} onRefresh={refreshRuns} />
            </div>
          </>
        )}
        {!loading && generator === 'recipe_attributes' && (
          <>
            <RecipeAttributesForm
              projects={projects}
              models={models}
              prompts={prompts}
              onPromptsChanged={refreshPrompts}
              onRunStarted={refreshRuns}
            />
            <div>
              <h3 className="text-sm font-semibold text-[#0C0B0B] mb-3">Run history</h3>
              <RunHistory runs={runs} models={models} onRefresh={refreshRuns} />
            </div>
          </>
        )}
        {!loading && generator === 'written_concept' && (
          <>
            <WrittenConceptForm
              projects={projects}
              models={models}
              prompts={prompts}
              onPromptsChanged={refreshPrompts}
              onRunStarted={refreshRuns}
            />
            <div>
              <h3 className="text-sm font-semibold text-[#0C0B0B] mb-3">Run history</h3>
              <RunHistory runs={runs} models={models} onRefresh={refreshRuns} />
            </div>
          </>
        )}
        {!loading && generator === 'visual_concept' && (
          <>
            <VisualConceptForm
              mode="generate"
              projects={projects}
              models={models}
              prompts={prompts}
              onPromptsChanged={refreshPrompts}
              onRunStarted={refreshRuns}
            />
            <div>
              <h3 className="text-sm font-semibold text-[#0C0B0B] mb-3">Run history</h3>
              <RunHistory runs={runs} models={models} onRefresh={refreshRuns} />
            </div>
          </>
        )}
        {!loading && generator === 'visual_concept_regenerate' && (
          <>
            <VisualConceptForm
              mode="regenerate"
              projects={projects}
              models={models}
              prompts={prompts}
              onPromptsChanged={refreshPrompts}
              onRunStarted={refreshRuns}
            />
            <div>
              <h3 className="text-sm font-semibold text-[#0C0B0B] mb-3">Run history</h3>
              <RunHistory runs={runs} models={models} onRefresh={refreshRuns} />
            </div>
          </>
        )}
        {!loading && generator === 'visual_concept_edit' && (
          <>
            <VisualEditForm
              projects={projects}
              models={models}
              onRunStarted={refreshRuns}
            />
            <div>
              <h3 className="text-sm font-semibold text-[#0C0B0B] mb-3">Run history</h3>
              <RunHistory runs={runs} models={models} onRefresh={refreshRuns} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AiTesterPanel;
